/**
 * A3 — Asignar una comunicación «Sin asignar» a un cliente (y opcionalmente a uno de sus
 * expedientes). Con un correo, puede crear además la regla «los correos de este remitente
 * van a este cliente» para que la próxima vez entre sola.
 */
import { useEffect, useState } from 'react'
import { Loader2, Search } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import Modal from '../ui/Modal'
import { inputCls } from '../communications/common'
import type {
  CaseListResponse,
  ClientListItem,
  ClientListResponse,
  UnassignedItem,
} from '@toga/shared'

interface Props {
  item: UnassignedItem
  /** Cliente ya elegido (una propuesta o un cliente recién creado). */
  initialClient?: { id: string; displayName: string } | null
  onClose: () => void
  onDone: (ruleCreated: boolean) => void
}

export default function AssignCommunicationModal({ item, initialClient, onClose, onDone }: Props) {
  const [client, setClient] = useState<{ id: string; displayName: string } | null>(
    initialClient ?? null
  )
  const [q, setQ] = useState('')
  const [results, setResults] = useState<ClientListItem[]>([])
  const [cases, setCases] = useState<CaseListResponse['items']>([])
  const [caseId, setCaseId] = useState('')
  const [createRule, setCreateRule] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (client || q.trim().length < 2) {
      setResults([])
      return
    }
    const t = setTimeout(() => {
      api
        .get<ClientListResponse>(`/clients?q=${encodeURIComponent(q.trim())}&pageSize=8`)
        .then((r) => setResults(r.items))
        .catch(() => setResults([]))
    }, 250)
    return () => clearTimeout(t)
  }, [q, client])

  useEffect(() => {
    setCaseId('')
    if (!client) {
      setCases([])
      return
    }
    api
      .get<CaseListResponse>(`/cases?clientId=${client.id}&pageSize=100`)
      .then((r) => setCases(r.items))
      .catch(() => setCases([]))
  }, [client])

  const canRule = item.source === 'EMAIL' && !!item.counterpartEmail

  const submit = async () => {
    if (!client) return
    setBusy(true)
    setError(null)
    try {
      const r = await api.post<{ ruleCreated: boolean }>(`/communications/${item.id}/assign`, {
        clientId: client.id,
        caseId: caseId || null,
        createRule: canRule && createRule,
      })
      onDone(r.ruleCreated)
    } catch (err) {
      const b = err instanceof ApiError ? (err.body as { error?: string } | undefined) : undefined
      setError(b?.error ?? (err instanceof Error ? err.message : 'No se pudo asignar'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Asignar a un cliente"
      description={item.subject || item.summary.slice(0, 120) || 'Comunicación'}
      maxWidth="max-w-lg"
    >
      <div className="space-y-4">
        {client ? (
          <div className="flex items-center justify-between gap-2 rounded-lg border border-toga-500/40 bg-toga-500/5 px-3 py-2">
            <span className="t-primary text-sm">{client.displayName}</span>
            {!initialClient && (
              <button
                type="button"
                onClick={() => setClient(null)}
                className="text-xs t-muted hover:underline"
              >
                Cambiar
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 t-faint" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Busca un cliente por nombre, NIF o email…"
                className={`${inputCls} pl-8`}
              />
            </div>
            {results.length > 0 && (
              <ul className="rounded-lg border border-base overflow-hidden">
                {results.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => setClient({ id: c.id, displayName: c.displayName })}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-hover border-b border-base/50 last:border-b-0"
                    >
                      <span className="t-primary">{c.displayName}</span>
                      <span className="t-faint text-xs ml-2">{c.nationalId ?? c.email ?? ''}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {item.proposals.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                <span className="t-faint text-xs">Propuestas:</span>
                {item.proposals.map((p) => (
                  <button
                    key={p.clientId}
                    type="button"
                    onClick={() => setClient({ id: p.clientId, displayName: p.displayName })}
                    title={p.label}
                    className="text-xs px-2 py-0.5 rounded-full border border-base t-secondary hover:bg-hover"
                  >
                    {p.displayName}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {client && cases.length > 0 && (
          <label className="text-xs t-muted block">
            Expediente (opcional)
            <select
              value={caseId}
              onChange={(e) => setCaseId(e.target.value)}
              className={`${inputCls} mt-1`}
            >
              <option value="">Solo al cliente (lo ve todo el bufete)</option>
              {cases.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} · {c.title}
                </option>
              ))}
            </select>
          </label>
        )}

        {canRule && client && (
          <label className="flex items-start gap-2 text-xs t-secondary cursor-pointer">
            <input
              type="checkbox"
              checked={createRule}
              onChange={(e) => setCreateRule(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              Archivar siempre en este {caseId ? 'expediente' : 'cliente'} los correos de{' '}
              <b>{item.counterpartEmail}</b> (crea una regla de archivado en esta cuenta).
            </span>
          </label>
        )}

        {error && (
          <div className="text-xs rounded-lg px-3 py-2 border text-rose-300 bg-rose-500/10 border-rose-500/30">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3.5 py-2 rounded-lg text-sm border border-base t-secondary hover:bg-hover"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={!client || busy}
            onClick={() => void submit()}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga disabled:opacity-40 flex items-center gap-2"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            Asignar
          </button>
        </div>
      </div>
    </Modal>
  )
}
